import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { MY_SOCIAL_CONNECTIONS } from '@/queries/queries';
import { PUBLISH_CLIP_TO_TIKTOK } from '@/queries/publishing';
import { isDirectPostConnection } from '@/lib/publishing/resolvePublishEligibility';
import { extractPublishErrorCode, mapPublishError } from '@/lib/publishing/mapPublishError';
import type { PublishConnection, PublishDialogTarget } from '@/lib/publishing/types';
import {
  TikTokCreatorStatus, TikTokPostInfoFields,
} from '@/components/publishing/TikTokPostInfoFields';
import { useTikTokPostInfo } from '@/hooks/use-tiktok-post-info';
import { useTikTokClipPreview } from '@/hooks/use-tiktok-clip-preview';
import { resolveTikTokClipBlock } from '@/lib/publishing/resolveTikTokClipBlock';
import { TikTokClipBlockNotice, TikTokClipPreview } from '@/components/publishing/TikTokClipPreview';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clipId: string;
  clipTitle: string;
  onPublished?: (target: PublishDialogTarget) => void;
}

const TITLE_MAX = 2200;

export const PublishToTikTokDialog: React.FC<Props> = ({
  open, onOpenChange, clipId, clipTitle, onPublished,
}) => {
  const { t } = useTranslation();
  const [connectionId, setConnectionId] = useState('');
  const [title, setTitle] = useState(clipTitle || '');
  const targetVersionRef = useRef(0);
  const mountedRef = useRef(false);
  const currentTargetRef = useRef({ open, clipId });
  currentTargetRef.current = { open, clipId };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const { data: connData } = useQuery(MY_SOCIAL_CONNECTIONS, { skip: !open });
  const [publish, { loading }] = useMutation(PUBLISH_CLIP_TO_TIKTOK);

  const connections: PublishConnection[] = useMemo(
    () => (connData?.mySocialConnections || [])
      .filter((c: PublishConnection) => c.platform === 'tiktok'),
    [connData],
  );
  const sendable = useMemo(() => connections.filter(isDirectPostConnection), [connections]);
  const selected = connectionId || sendable[0]?.id || '';
  const selectedConnection = sendable.find((connection) => connection.id === selected);
  const postInfo = useTikTokPostInfo({ open, socialConnectionId: selected });
  const { reset: resetPostInfo } = postInfo;
  const preview = useTikTokClipPreview({ open, clipId });
  const clipBlock = resolveTikTokClipBlock({
    watermark: preview.watermark,
    durationSeconds: preview.durationSeconds,
    maxDurationSeconds: postInfo.creator?.max_video_post_duration_sec,
  });

  useEffect(() => {
    targetVersionRef.current += 1;
    setConnectionId('');
    setTitle(clipTitle || '');
    resetPostInfo();
  }, [clipId, clipTitle, open, resetPostInfo]);


  const close = () => {
    targetVersionRef.current += 1;
    onOpenChange(false);
  };

  const goToSettings = () => {
    close();
    window.dispatchEvent(new CustomEvent('autoclipper:open-account-settings', {
      detail: { section: 'social', platform: 'tiktok' },
    }));
  };

  const canPublish = !!selected
    && !clipBlock
    && postInfo.isComplete
    && title.length <= TITLE_MAX
    && !loading;

  const handlePublish = async () => {
    if (!canPublish) return;
    const submittedClipId = clipId;
    const submittedVersion = targetVersionRef.current;
    const submittedTarget: PublishDialogTarget = { clipId: submittedClipId, platform: 'tiktok' };
    const isCurrentSubmission = () => (
      mountedRef.current
      && submittedVersion === targetVersionRef.current
      && currentTargetRef.current.open
      && currentTargetRef.current.clipId === submittedClipId
    );

    try {
      const result = await publish({
        variables: {
          clipId: submittedClipId,
          socialConnectionId: selected,
          title: title || null,
          ...postInfo.variables,
        },
      });
      const record = result.data?.publishClipToTikTok;
      const accepted = !!record && record.status !== 'failed';
      const submissionWasCurrent = isCurrentSubmission();
      if (accepted) onPublished?.(submittedTarget);
      if (!submissionWasCurrent) return;

      if (record?.status === 'failed') {
        toast.error(t(mapPublishError(record.fail_reason)));
        return;
      }
      toast.success(t('publishing.tiktok.success'));
      close();
    } catch (err) {
      if (!isCurrentSubmission()) return;
      toast.error(t(mapPublishError(extractPublishErrorCode(err))));
    }
  };

  const body = () => {
    if (connections.length === 0) {
      return (
        <div className="space-y-3" data-testid="publish-no-connection">
          <p className="text-sm text-muted-foreground">{t('publishing.tiktok.noConnectionBody')}</p>
          <Button type="button" onClick={goToSettings}>{t('publishing.reconnectAction')}</Button>
        </div>
      );
    }
    if (sendable.length === 0) {
      return (
        <div className="space-y-3" data-testid="publish-reconnect">
          <p className="text-sm text-muted-foreground">{t('publishing.tiktok.reconnectBody')}</p>
          <Button type="button" onClick={goToSettings}>{t('publishing.reconnectAction')}</Button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="tiktok-direct-account">{t('publishing.accountLabel')}</Label>
          <Select value={selected} onValueChange={setConnectionId}>
            <SelectTrigger id="tiktok-direct-account" data-testid="tiktok-account-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sendable.map((connection) => (
                <SelectItem key={connection.id} value={connection.id}>
                  {connection.display_name || connection.username}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <TikTokCreatorStatus
          postInfo={postInfo}
          fallbackName={selectedConnection?.display_name || selectedConnection?.username}
          onReconnect={goToSettings}
        />

        <TikTokClipPreview preview={preview} />
        <TikTokClipBlockNotice block={clipBlock} />

        <div className="space-y-2">
          <Label htmlFor="tiktok-title">{t('publishing.tiktok.titleLabel')}</Label>
          <Textarea
            id="tiktok-title"
            data-testid="tiktok-title"
            value={title}
            maxLength={TITLE_MAX}
            onChange={(event) => setTitle(event.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            {t('publishing.tiktok.characterCount', { current: title.length, max: TITLE_MAX })}
          </p>
        </div>

        <TikTokPostInfoFields postInfo={postInfo}>
          <p className="text-xs text-muted-foreground">{t('publishing.tiktok.processingNote')}</p>
        </TikTokPostInfoFields>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="publish-to-tiktok-dialog">
        <DialogHeader>
          <DialogTitle>{t('publishing.tiktok.dialogTitle')}</DialogTitle>
          <DialogDescription>{t('publishing.tiktok.dialogDescription')}</DialogDescription>
        </DialogHeader>
        {body()}
        {sendable.length > 0 && (
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close} disabled={loading}>
              {t('publishing.tiktok.cancel')}
            </Button>
            <Button
              type="button"
              onClick={() => { void handlePublish(); }}
              disabled={!canPublish}
              data-testid="tiktok-direct-publish"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
              {loading ? t('publishing.tiktok.publishing') : t('publishing.tiktok.publish')}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PublishToTikTokDialog;
